/**
 * Ported VERBATIM from apr_template `src/pages/DispositionControl.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect, useRef } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import './ApplicationDependencies.css';
import { useAuth } from '../compat/AuthContext';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, panels and token pairs.
import { Banner, Icon, IconButton, Sheet, icons } from '@platform/ui-kit/workspace';
import { WS_CHIP, WS_COLOR, WS_RADIUS, WS_STATUS, wsTone, wsType, type WsTone } from '../workspace/ws';
import { useWsDialogs } from '../workspace/useWsDialogs';

const DispositionControlHelp: Help = {
  title: 'Roadmap Approval',
  icon: '🚦',
  purpose: 'Approve applications onto the implementation roadmap and track each app&rsquo;s deployment status within its integration group. Status changes here gate whether a schedule can still be edited downstream.',
  doHere: [
    'Pick a <b>Grouping ID</b> (and optionally Application ID / Status) to load its records.',
    'Click <b>+ Add Status Record</b> to start tracking an application&rsquo;s deployment status.',
    'Use the <b>edit</b> action to advance status along the allowed transitions.',
    'Use <b>view</b> to see previous status, who changed it, and the change reason.',
    'Add a <b>change reason</b> when moving an app to a new status for an audit trail.',
  ],
  fits: 'Integration &rarr; Implementation Planning &rarr; Roadmap Approval',
  firstTime: 'Select a Grouping ID from the filter, then add or approve its applications to move them onto the roadmap.',
  sections: [
    {
      type: 'defs',
      label: 'Deployment statuses',
      items: [
        { term: 'Need Approval', color: '#92400e', def: 'Starting state &mdash; awaiting sign-off before it joins the roadmap.' },
        { term: 'Approved', color: '#1e40af', def: 'Cleared for the roadmap; can move forward to In Development or back to Need Approval.' },
        { term: 'In Development', color: '#3730a3', def: 'Work underway; next step is Deployed.' },
        { term: 'Deployed', color: '#065f46', def: 'Completed and live &mdash; the record becomes read-only.' },
      ],
      note: 'Transitions are enforced: Need Approval &rarr; Approved &rarr; In Development &rarr; Deployed (Approved can revert to Need Approval).',
    },
  ],
};

interface DeploymentStatusRecord {
  id: number;
  integration_group_id: string;
  application_id: string;
  deployment_status: 'Need Approval' | 'Approved' | 'In Development' | 'Deployed';
  changed_by_user: string | null;
  change_date: string | null;
  previous_status: string | null;
  change_reason: string | null;
  group_name: string | null;
  application_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface IntegrationGroup {
  integration_group_id: string;
  integration_group_description: string;
  application_count: number;
}

interface ApplicationOption {
  application_id: string;
  description: string;
  disposition?: string;
}

// Status colors — DW: the source's amber/blue/indigo/green/grey pairs mapped to the kit's
// measured status pairs (indigo has no kit pair; it shares the blue `info` pair with Approved).
const getStatusTone = (status: string): WsTone => {
  switch (status) {
    case 'Need Approval': return 'warning';
    case 'Approved': return 'info';
    case 'In Development': return 'info';
    case 'Deployed': return 'success';
    default: return 'neutral';
  }
};

// Status transition rules
const STATUS_TRANSITIONS: Record<string, string[]> = {
  'Need Approval': ['Approved'],
  'Approved': ['Need Approval', 'In Development'],
  'In Development': ['Deployed'],
  'Deployed': []
};

const DispositionControl: React.FC = () => {
  const { currentUser } = useAuth();
  const [records, setRecords] = useState<DeploymentStatusRecord[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [applications, setApplications] = useState<ApplicationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [filterGroupingId, setFilterGroupingId] = useState<string>('');
  const [filterApplicationId, setFilterApplicationId] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [editingRecord, setEditingRecord] = useState<DeploymentStatusRecord | null>(null);
  const [viewingRecord, setViewingRecord] = useState<DeploymentStatusRecord | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAppDropdown, setShowAppDropdown] = useState(false);
  const { confirm, dialogs } = useWsDialogs();
  const [formData, setFormData] = useState({
    integrationGroupId: '',
    applicationId: '',
    deploymentStatus: 'Need Approval' as string,
    changeReason: ''
  });

  const getUserFullName = () => {
    if (currentUser?.name) {
      return currentUser.name;
    } else if (currentUser?.email) {
      return currentUser.email;
    }
    return 'Unknown User';
  };

  useEffect(() => {
    fetchRecords();
    fetchIntegrationGroups();
  }, []);

  useEffect(() => {
    if (formData.integrationGroupId) {
      fetchApplicationsForGroup(formData.integrationGroupId);
    } else {
      setApplications([]);
    }
  }, [formData.integrationGroupId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.custom-dropdown')) {
        setShowAppDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await authFetch('/api/deployment-status');
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch');
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setRecords(data);
      } else {
        setRecords([]);
      }
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching deployment status records:', err);
      setRecords([]);
      setLoading(false);
    }
  };

  const fetchIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/application-dependencies/lookup/integration-groups?scope=planning');
      const result = await response.json();
      if (result.success) {
        setIntegrationGroups(result.data || []);
      }
    } catch (err) {
      console.error('Error loading integration groups:', err);
    }
  };

  const fetchApplicationsForGroup = async (groupId: string) => {
    try {
      const response = await authFetch(`/api/application-dependencies/lookup/applications/${groupId}`);
      const result = await response.json();
      if (result.success) {
        setApplications(result.data || []);
      }
    } catch (err) {
      console.error('Error loading applications:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.integrationGroupId) {
      setError('Integration Group is required');
      return;
    }

    if (!formData.applicationId) {
      setError('Application is required');
      return;
    }

    try {
      if (editingRecord) {
        // Update existing record
        const response = await authFetch(`/api/deployment-status/${editingRecord.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deployment_status: formData.deploymentStatus,
            changed_by_user: getUserFullName(),
            change_reason: formData.changeReason || null
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update');
        }

        setSuccess('Deployment status updated successfully');
      } else {
        // Create new record
        const response = await authFetch('/api/deployment-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            integration_group_id: formData.integrationGroupId,
            application_id: formData.applicationId,
            deployment_status: formData.deploymentStatus,
            changed_by_user: getUserFullName(),
            change_reason: formData.changeReason || null
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create');
        }

        setSuccess('Deployment status record created successfully');
      }

      fetchRecords();
      handleCloseForm();
    } catch (err: any) {
      console.error('Failed to save deployment status:', err);
      setError(err.message || 'Failed to save deployment status');
    }
  };

  const handleEdit = (record: DeploymentStatusRecord) => {
    setEditingRecord(record);
    setFormData({
      integrationGroupId: record.integration_group_id,
      applicationId: record.application_id,
      deploymentStatus: record.deployment_status,
      changeReason: ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (await confirm('Are you sure you want to delete this deployment status record?', { tone: 'destructive' })) {
      try {
        const response = await authFetch(`/api/deployment-status/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete');
        }
        setSuccess('Deployment status record deleted successfully');
        fetchRecords();
      } catch (err: any) {
        setError(err.message || 'Failed to delete deployment status record');
      }
    }
  };

  const handleView = (record: DeploymentStatusRecord) => {
    setViewingRecord(record);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setViewingRecord(null);
    setShowViewModal(false);
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    setFormData({
      integrationGroupId: '',
      applicationId: '',
      deploymentStatus: 'Need Approval',
      changeReason: ''
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRecord(null);
    setFormData({
      integrationGroupId: '',
      applicationId: '',
      deploymentStatus: 'Need Approval',
      changeReason: ''
    });
    setError('');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get unique grouping IDs from records for filter dropdown
  const uniqueGroupingIds = Array.from(new Set(records.map(r => r.integration_group_id).filter(Boolean))).sort();

  // Get application IDs filtered by selected grouping
  const filteredApplicationIds = Array.from(new Set(
    records
      .filter(r => !filterGroupingId || filterGroupingId === '__ALL__' || r.integration_group_id === filterGroupingId)
      .map(r => r.application_id)
      .filter(Boolean)
  )).sort();

  const filteredRecords = records.filter(record => {
    // If no grouping is selected, show no records
    if (!filterGroupingId) return false;
    // If "All Grouping IDs" is selected, show all groups
    const groupMatch = filterGroupingId === '__ALL__' || record.integration_group_id === filterGroupingId;
    const appMatch = !filterApplicationId || record.application_id === filterApplicationId;
    const statusMatch = !filterStatus || record.deployment_status === filterStatus;
    return groupMatch && appMatch && statusMatch;
  });

  const getAvailableTransitions = (currentStatus: string): string[] => {
    return STATUS_TRANSITIONS[currentStatus] || [];
  };

  // DW: shared inline shapes. The field look (36px, 8px corners, 1px outline, 14px) comes from
  // pp-workspace.css; only width, the read-only fill and the cursor stay inline.
  const cellStyle = (textAlign: 'left' | 'center', nowrap = true): React.CSSProperties => ({ textAlign, ...(nowrap ? { whiteSpace: 'nowrap' } : null) });
  const subText: React.CSSProperties = { ...wsType('table'), color: WS_COLOR.textMuted };
  const formLabel: React.CSSProperties = { display: 'block', ...wsType('label'), marginBottom: '8px' };
  const viewLabel: React.CSSProperties = { ...wsType('label'), color: WS_COLOR.textMuted, display: 'block', marginBottom: '4px' };
  const viewValue: React.CSSProperties = { ...wsType('body') };
  const statusChip = (status: string): React.CSSProperties => ({ ...WS_CHIP, ...wsTone(getStatusTone(status)) });

  return (
    <div className="page" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <HelpPanel help={DispositionControlHelp} />
      {/* Header */}
      <PageHeader title={<>Roadmap Approval</>} subtitle={<>Approve applications for the roadmap and manage their deployment status across integration groups</>} />
      <div className="page-header__actions" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
<button
              className="btn btn-primary"
              onClick={handleAddNew}
              title="Create new deployment status record"
            >
              <Icon icon={icons.Plus} size={20} /> Add Status Record
            </button>
      </div>

      {loading && (
        <div className="md-loading" style={{ padding: '40px', textAlign: 'center' }}>
          Loading deployment status records...
        </div>
      )}

      {!loading && (
        <>
          {/* Filter Section - Sticky below header */}
          <div style={{ flexShrink: 0, padding: '0 16px', marginBottom: '16px' }}>
            <div className="md-card" style={{ padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                {/* Grouping ID Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ ...wsType('label'), minWidth: '100px' }}>Grouping ID:</label>
                  <select
                    value={filterGroupingId}
                    onChange={(e) => {
                      setFilterGroupingId(e.target.value);
                      setFilterApplicationId(''); // Reset Application ID when Grouping changes
                    }}
                    style={{ minWidth: '250px' }}
                  >
                    <option value="">Select Grouping</option>
                    <option value="__ALL__">All Grouping IDs</option>
                    {uniqueGroupingIds.map(groupId => (
                      <option key={groupId} value={groupId}>{groupId}</option>
                    ))}
                  </select>
                </div>

                {/* Application ID Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ ...wsType('label'), minWidth: '100px' }}>Application ID:</label>
                  <select
                    value={filterApplicationId}
                    onChange={(e) => setFilterApplicationId(e.target.value)}
                    disabled={!filterGroupingId}
                    style={{
                      minWidth: '250px',
                      backgroundColor: !filterGroupingId ? WS_COLOR.surfaceSubtle : WS_COLOR.surface,
                      cursor: !filterGroupingId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">All Application IDs</option>
                    {filteredApplicationIds.map(appId => (
                      <option key={appId} value={appId}>{appId}</option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ ...wsType('label'), minWidth: '100px' }}>Status:</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    disabled={!filterGroupingId}
                    style={{
                      minWidth: '200px',
                      backgroundColor: !filterGroupingId ? WS_COLOR.surfaceSubtle : WS_COLOR.surface,
                      cursor: !filterGroupingId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">All Statuses</option>
                    <option value="Need Approval">Need Approval</option>
                    <option value="Approved">Approved</option>
                    <option value="In Development">In Development</option>
                    <option value="Deployed">Deployed</option>
                  </select>
                </div>

                {/* Results counter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
                  <span style={{ ...wsType('label'), color: WS_COLOR.textMuted }}>
                    {!filterGroupingId
                      ? 'Select a Grouping to view data'
                      : `Showing ${filteredRecords.length} of ${records.length} records`
                    }
                  </span>
                  {filterGroupingId && (
                    <button
                      className="btn"
                      onClick={() => {
                        setFilterGroupingId('');
                        setFilterApplicationId('');
                        setFilterStatus('');
                      }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Data Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>
            {/* Main Content Card */}
          <div className="md-card" style={{ overflow: 'visible', minHeight: 'auto' }}>
            {!filterGroupingId ? (
              <div className="md-empty-state" style={{ padding: '40px' }}>
                <div className="md-empty-icon"><Icon icon={icons.ClipboardList} size={24} /></div>
                <h3>Select a Grouping ID to View Data</h3>
                <p>Please select a Grouping ID from the dropdown above to view deployment status records.</p>
              </div>
            ) : filteredRecords.length === 0 && records.length === 0 ? (
              <div className="md-empty-state" style={{ padding: '40px' }}>
                <div className="md-empty-icon"><Icon icon={icons.Rocket} size={24} /></div>
                <h3>No Deployment Status Records Found</h3>
                <p>Click "Add Status Record" to create deployment status tracking for applications</p>
              </div>
            ) : filteredRecords.length === 0 ? (
              <div className="md-empty-state" style={{ padding: '40px' }}>
                <div className="md-empty-icon"><Icon icon={icons.SearchX} size={24} /></div>
                <h3>No Records Match Filters</h3>
                <p>Try adjusting your filter criteria or clear filters to see all records.</p>
              </div>
            ) : (
              <div className="md-table-container" style={{ overflowY: 'visible', height: 'auto', maxHeight: 'none' }}>
                <table className="md-table">
                  <thead>
                    <tr>
                      <th style={cellStyle('left')}>Grouping ID</th>
                      <th style={cellStyle('left')}>Application ID</th>
                      <th style={cellStyle('center')}>Deployment Status</th>
                      <th style={cellStyle('left')}>Changed By</th>
                      <th style={cellStyle('left')}>Change Date</th>
                      <th style={cellStyle('center')}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map(record => (
                      <tr key={record.id}>
                        <td style={cellStyle('left')}>
                          <div>
                            <div style={{ fontWeight: 500 }}>{record.integration_group_id}</div>
                            {record.group_name && (
                              <div style={subText}>{record.group_name}</div>
                            )}
                          </div>
                        </td>
                        <td style={cellStyle('left')}>
                          <div>
                            <div style={{ fontWeight: 500 }}>{record.application_id}</div>
                            {record.application_name && (
                              <div style={subText}>{record.application_name}</div>
                            )}
                          </div>
                        </td>
                        <td style={cellStyle('center', false)}>
                          <span style={statusChip(record.deployment_status)}>
                            {record.deployment_status}
                          </span>
                        </td>
                        <td style={cellStyle('left')}>
                          {record.changed_by_user || '-'}
                        </td>
                        <td style={cellStyle('left')}>
                          {formatDate(record.change_date)}
                        </td>
                        <td style={cellStyle('center')}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                            <IconButton
                              icon={icons.Eye}
                              label="View details"
                              title="View details"
                              onClick={() => handleView(record)}
                            />
                            <IconButton
                              icon={icons.Pencil}
                              label="Edit status"
                              title="Edit status"
                              disabled={record.deployment_status === 'Deployed'}
                              onClick={() => handleEdit(record)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>

          {/* Form — a right-side panel (Decision Workspace Sheet, single width). */}
          <Sheet
            open={showForm}
            title={editingRecord ? 'Update Deployment Status' : 'Add Deployment Status'}
            onClose={handleCloseForm}
            width={531}
            actions={
              <>
                <button type="button" className="btn" onClick={handleCloseForm}>
                  Cancel
                </button>
                <button type="submit" form="dc-status-form" className="btn btn-primary">
                  {editingRecord ? 'Update Status' : 'Create Status Record'}
                </button>
              </>
            }
          >
                  <form id="dc-status-form" onSubmit={handleSubmit}>
                    {error && (
                      <div style={{ marginBottom: '16px' }}>
                        <Banner tone="error">{error}</Banner>
                      </div>
                    )}

                    {/* Integration Group */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={formLabel}>
                        Integration Group *
                      </label>
                      <select
                        value={formData.integrationGroupId}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            integrationGroupId: e.target.value,
                            applicationId: ''
                          });
                        }}
                        disabled={!!editingRecord}
                        style={{
                          width: '100%',
                          backgroundColor: editingRecord ? WS_COLOR.surfaceSubtle : WS_COLOR.surface,
                          cursor: editingRecord ? 'not-allowed' : 'pointer'
                        }}
                        required
                      >
                        <option value="">Select Integration Group</option>
                        {integrationGroups.map(group => (
                          <option key={group.integration_group_id} value={group.integration_group_id}>
                            {group.integration_group_id} - {group.integration_group_description} ({group.application_count} apps)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Application */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={formLabel}>
                        Application *
                      </label>
                      {editingRecord ? (
                        <input
                          type="text"
                          value={`${formData.applicationId}${editingRecord.application_name ? ` - ${editingRecord.application_name}` : ''}`}
                          disabled
                          style={{
                            width: '100%',
                            backgroundColor: WS_COLOR.surfaceSubtle,
                            cursor: 'not-allowed'
                          }}
                        />
                      ) : (
                        // DW: `.custom-dropdown` is drawn as the 36px field by pp-workspace.css, so the
                        // trigger inside it carries no border or padding of its own.
                        <div
                          className="custom-dropdown"
                          style={{
                            position: 'relative',
                            backgroundColor: !formData.integrationGroupId ? WS_COLOR.surfaceSubtle : WS_COLOR.surface
                          }}
                        >
                          <div
                            onClick={() => {
                              if (formData.integrationGroupId) {
                                setShowAppDropdown(!showAppDropdown);
                              }
                            }}
                            style={{
                              height: '100%',
                              cursor: formData.integrationGroupId ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <span style={{ color: formData.applicationId ? WS_COLOR.text : WS_COLOR.textMuted }}>
                              {formData.applicationId && applications.find(a => a.application_id === formData.applicationId)
                                ? `${applications.find(a => a.application_id === formData.applicationId)!.application_id} - ${applications.find(a => a.application_id === formData.applicationId)!.description}`
                                : (formData.integrationGroupId ? 'Select Application' : 'Select Integration Group first')}
                            </span>
                            {formData.integrationGroupId && <Icon icon={icons.ChevronDown} size={16} />}
                          </div>
                          {showAppDropdown && formData.integrationGroupId && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: WS_COLOR.surface,
                              border: `1px solid ${WS_COLOR.border}`,
                              borderRadius: WS_RADIUS.field,
                              boxShadow: WS_COLOR.menuShadow,
                              zIndex: 1000,
                              maxHeight: '200px',
                              overflowY: 'auto'
                            }}>
                              {applications.length === 0 ? (
                                <div style={{ padding: '12px 16px', color: WS_COLOR.textMuted }}>
                                  No applications available
                                </div>
                              ) : (
                                applications.map(app => (
                                  <div
                                    key={app.application_id}
                                    onClick={() => {
                                      setFormData({ ...formData, applicationId: app.application_id });
                                      setShowAppDropdown(false);
                                    }}
                                    style={{
                                      padding: '12px 16px',
                                      cursor: 'pointer',
                                      borderBottom: `1px solid ${WS_COLOR.border}`,
                                      ...(formData.applicationId === app.application_id ? wsTone('info') : { backgroundColor: WS_COLOR.surface, color: WS_COLOR.text })
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = WS_COLOR.tonalHover)}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.applicationId === app.application_id ? WS_STATUS.info.fill : WS_COLOR.surface)}
                                  >
                                    <strong>{app.application_id}</strong> - {app.description}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Deployment Status */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={formLabel}>
                        Deployment Status *
                      </label>
                      <select
                        value={formData.deploymentStatus}
                        onChange={(e) => setFormData({ ...formData, deploymentStatus: e.target.value })}
                        style={{
                          width: '100%',
                          cursor: 'pointer'
                        }}
                        required
                      >
                        {editingRecord ? (
                          // Show only valid transitions when editing
                          <>
                            <option value={editingRecord.deployment_status}>{editingRecord.deployment_status} (current)</option>
                            {getAvailableTransitions(editingRecord.deployment_status).map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </>
                        ) : (
                          // Show all options when creating
                          <>
                            <option value="Need Approval">Need Approval</option>
                            <option value="Approved">Approved</option>
                            <option value="In Development">In Development</option>
                            <option value="Deployed">Deployed</option>
                          </>
                        )}
                      </select>
                      {editingRecord && (
                        <p style={{ ...wsType('table'), color: WS_COLOR.textMuted, marginTop: '8px' }}>
                          Allowed transitions from "{editingRecord.deployment_status}": {getAvailableTransitions(editingRecord.deployment_status).join(', ') || 'None'}
                        </p>
                      )}
                    </div>

                    {/* Change Reason (only for edits) */}
                    {editingRecord && formData.deploymentStatus !== editingRecord.deployment_status && (
                      <div style={{ marginBottom: '20px' }}>
                        <label style={formLabel}>
                          Change Reason
                        </label>
                        <textarea
                          value={formData.changeReason}
                          onChange={(e) => setFormData({ ...formData, changeReason: e.target.value })}
                          placeholder="Optional: Provide a reason for this status change"
                          style={{
                            width: '100%',
                            minHeight: '80px',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    )}

                    {/* Cancel and Create/Update are in the panel's fixed action row (Sheet `actions`). */}
                  </form>
          </Sheet>

          {/* View — a right-side panel (Decision Workspace Sheet, single width). */}
          <Sheet
            open={showViewModal && !!viewingRecord}
            title="Deployment Status Details"
            onClose={handleCloseViewModal}
            width={531}
            actions={
              <button type="button" className="btn" onClick={handleCloseViewModal}>
                Close
              </button>
            }
          >
            {viewingRecord && (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={viewLabel}>Integration Group ID</label>
                        <div style={{ ...viewValue, fontWeight: 500 }}>{viewingRecord.integration_group_id}</div>
                        {viewingRecord.group_name && (
                          <div style={subText}>{viewingRecord.group_name}</div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={viewLabel}>Application ID</label>
                        <div style={{ ...viewValue, fontWeight: 500 }}>{viewingRecord.application_id}</div>
                        {viewingRecord.application_name && (
                          <div style={subText}>{viewingRecord.application_name}</div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label style={viewLabel}>Current Deployment Status</label>
                      <span style={statusChip(viewingRecord.deployment_status)}>
                        {viewingRecord.deployment_status}
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={viewLabel}>Changed By</label>
                        <div style={viewValue}>{viewingRecord.changed_by_user || '-'}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={viewLabel}>Change Date</label>
                        <div style={viewValue}>{formatDate(viewingRecord.change_date)}</div>
                      </div>
                    </div>

                    {viewingRecord.previous_status && (
                      <div>
                        <label style={viewLabel}>Previous Status</label>
                        <div style={viewValue}>{viewingRecord.previous_status}</div>
                      </div>
                    )}

                    {viewingRecord.change_reason && (
                      <div>
                        <label style={viewLabel}>Change Reason</label>
                        <div style={{ ...viewValue, padding: '8px', backgroundColor: WS_COLOR.surfaceSubtle, borderRadius: WS_RADIUS.field }}>
                          {viewingRecord.change_reason}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={viewLabel}>Created At</label>
                        <div style={viewValue}>{formatDate(viewingRecord.created_at)}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={viewLabel}>Last Updated</label>
                        <div style={viewValue}>{formatDate(viewingRecord.updated_at)}</div>
                      </div>
                    </div>
                  </div>
            )}
          </Sheet>

          {/* Success/Error Messages */}
          {success && (
            <div className="md-toast md-toast--success">
              {success}
              <button onClick={() => setSuccess('')} aria-label="Dismiss"><Icon icon={icons.X} size={16} /></button>
            </div>
          )}
        </>
      )}
      {dialogs}
    </div>
  );
};

export default DispositionControl;
